import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Database,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Trash2,
  Workflow,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge, type BadgeColor } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import type {
  ClaimsPanopticonCoverageAnalysis,
  ClaimsPanopticonMatterSummary,
  ClaimsPanopticonRun,
} from '@/services/api/claimsPanopticonService';
import {
  useClearClaimsPanopticonMutation,
  useDeleteClaimsPanopticonRunMutation,
  useGetClaimsPanopticonRunDetailQuery,
  useGetClaimsPanopticonRunsQuery,
} from '@/services/api/claimsPanopticonService';

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function truncate(value: string | null | undefined, max = 180): string {
  if (!value) return '—';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

function statusBadgeColor(status: string): BadgeColor {
  switch (status) {
    case 'completed':
      return 'green';
    case 'completed_with_errors':
      return 'amber';
    case 'failed':
      return 'red';
    case 'running':
      return 'blue';
    default:
      return 'zinc';
  }
}

function severityBadgeColor(severity: string): BadgeColor {
  switch (severity) {
    case 'critical':
      return 'red';
    case 'high':
      return 'orange';
    case 'medium':
      return 'amber';
    case 'low':
      return 'green';
    default:
      return 'zinc';
  }
}

const JsonPanel: React.FC<{
  label: string;
  value: unknown;
  defaultOpen?: boolean;
}> = ({ label, value, defaultOpen = false }) => (
  <details
    className="rounded-xl border border-zinc-200 bg-zinc-950/[0.02] p-3 dark:border-zinc-800 dark:bg-white/[0.03]"
    open={defaultOpen}
  >
    <summary className="cursor-pointer text-sm font-medium text-zinc-700 dark:text-zinc-300">
      {label}
    </summary>
    <pre className="mt-3 overflow-x-auto rounded-lg bg-zinc-950 px-3 py-3 text-xs leading-5 text-zinc-100">
      {JSON.stringify(value ?? null, null, 2)}
    </pre>
  </details>
);

const StatCard: React.FC<{
  label: string;
  value: string | number;
  tone?: 'default' | 'attention';
}> = ({ label, value, tone = 'default' }) => (
  <div
    className={[
      'rounded-2xl border p-4',
      tone === 'attention'
        ? 'border-orange-200 bg-orange-50 dark:border-orange-900/60 dark:bg-orange-950/30'
        : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900',
    ].join(' ')}
  >
    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
      {label}
    </div>
    <div className="mt-2 text-2xl font-semibold text-zinc-950 dark:text-zinc-50">
      {value}
    </div>
  </div>
);

const COVERAGES_PER_PAGE = 20;
const MATTERS_PER_PAGE = 20;

const PaginationControls = ({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 pt-2">
      <Button
        outline
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft data-slot="icon" />
        Previous
      </Button>
      <span className="text-sm text-zinc-500 dark:text-zinc-400">
        Page {page} of {totalPages}
      </span>
      <Button
        outline
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        Next
        <ChevronRight data-slot="icon" />
      </Button>
    </div>
  );
}

function resolveAnalysisSummary(
  analysis: ClaimsPanopticonCoverageAnalysis,
  classification: Record<string, unknown>,
): string {
  if (analysis.analysis_summary) return truncate(analysis.analysis_summary, 360);

  const classificationSummary = classification.overall_summary;
  if (typeof classificationSummary === 'string' && classificationSummary) {
    return truncate(classificationSummary, 360);
  }

  if (analysis.llm_error) return `Analysis failed: ${truncate(analysis.llm_error, 300)}`;

  return 'No analysis summary available.';
}

const CoverageRow: React.FC<{ analysis: ClaimsPanopticonCoverageAnalysis }> = ({
  analysis,
}) => {
  const classification = asRecord(analysis.classification);
  const extractedContext = asRecord(analysis.extracted_context);
  const blockers = asStringArray(classification.top_blockers);
  const carrier =
    typeof extractedContext.insurance_carrier === 'string'
      ? extractedContext.insurance_carrier
      : 'Unknown carrier';
  const firmName =
    typeof extractedContext.firm_name === 'string'
      ? extractedContext.firm_name
      : 'Unknown firm';
  const state =
    typeof extractedContext.state === 'string' ? extractedContext.state : '—';
  const partyType =
    typeof extractedContext.party_type === 'string'
      ? extractedContext.party_type
      : 'unclear';
  const summaryText = resolveAnalysisSummary(analysis, classification);
  const isError = !analysis.analysis_summary && !classification.overall_summary && !!analysis.llm_error;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge color={severityBadgeColor(analysis.severity)}>
              {analysis.severity}
            </Badge>
            {analysis.needs_attention ? (
              <Badge color="orange">Needs attention</Badge>
            ) : (
              <Badge color="green">Stable</Badge>
            )}
            <Badge color="zinc">{analysis.current_stage || 'Unknown stage'}</Badge>
            <Badge color="blue">{analysis.primary_owner}</Badge>
          </div>
          <div className="mt-3 text-sm font-semibold text-zinc-950 dark:text-zinc-50">
            Coverage{' '}
            <span className="font-mono text-xs text-zinc-500 dark:text-zinc-400">
              {analysis.coverage_id}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-600 dark:text-zinc-300">
            <span>{firmName}</span>
            <span>{carrier}</span>
            <span>{state}</span>
            <span>{partyType}</span>
            <Link
              to={`/matters/${analysis.matter_id}`}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Open matter
            </Link>
          </div>
        </div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          Updated {formatDateTime(analysis.modified_at)}
        </div>
      </div>

      {isError ? (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm leading-6 text-red-700 dark:text-red-300">
            {summaryText}
          </p>
        </div>
      ) : (
        <p className="mt-4 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
          {summaryText}
        </p>
      )}

      {blockers.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {blockers.slice(0, 4).map((blocker) => (
            <Badge key={blocker} color="amber">
              {blocker}
            </Badge>
          ))}
        </div>
      ) : null}

      <div className="mt-4 grid gap-3 xl:grid-cols-2">
        <JsonPanel label="Classification JSON" value={analysis.classification} />
        <JsonPanel label="Extracted context" value={analysis.extracted_context} />
      </div>
    </div>
  );
};

const MatterSummaryRow: React.FC<{ summary: ClaimsPanopticonMatterSummary }> = ({
  summary,
}) => {
  const payload = asRecord(summary.summary);
  const followups = asStringArray(payload.recommended_followups);

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge color={severityBadgeColor(summary.severity)}>
              {summary.severity}
            </Badge>
            {summary.needs_attention ? (
              <Badge color="orange">Needs attention</Badge>
            ) : (
              <Badge color="green">Stable</Badge>
            )}
            <Badge color="zinc">{summary.current_stage || 'Unknown stage'}</Badge>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-600 dark:text-zinc-300">
            <span className="font-mono text-xs">{summary.matter_id}</span>
            <Link
              to={`/matters/${summary.matter_id}`}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Open matter
            </Link>
          </div>
        </div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          Updated {formatDateTime(summary.modified_at)}
        </div>
      </div>

      <p className="mt-4 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
        {truncate(summary.slack_summary, 320)}
      </p>

      {followups.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {followups.slice(0, 4).map((followup) => (
            <Badge key={followup} color="blue">
              {followup}
            </Badge>
          ))}
        </div>
      ) : null}

      <div className="mt-4">
        <JsonPanel label="Matter synthesis JSON" value={summary.summary} />
      </div>
    </div>
  );
};

type ConfirmAction =
  | { kind: 'delete_run'; run: ClaimsPanopticonRun }
  | { kind: 'clear_all' }
  | null;

export const ClaimsPanopticonPage: React.FC = () => {
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);
  const [confirmAction, setConfirmAction] = React.useState<ConfirmAction>(null);
  const [coveragePage, setCoveragePage] = React.useState(1);
  const [matterPage, setMatterPage] = React.useState(1);

  const {
    data: runsData,
    isLoading: isLoadingRuns,
    isFetching: isFetchingRuns,
    refetch: refetchRuns,
  } = useGetClaimsPanopticonRunsQuery(50);
  const runs = React.useMemo(() => runsData?.runs ?? [], [runsData?.runs]);

  React.useEffect(() => {
    if (runs.length === 0) {
      setSelectedRunId(null);
      return;
    }

    if (!selectedRunId || !runs.some((run) => run.id === selectedRunId)) {
      setSelectedRunId(runs[0].id);
    }
  }, [runs, selectedRunId]);

  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? null;

  // Reset pagination when the selected run changes
  React.useEffect(() => {
    setCoveragePage(1);
    setMatterPage(1);
  }, [selectedRunId]);

  const {
    data: runDetail,
    isLoading: isLoadingDetail,
    isFetching: isFetchingDetail,
  } = useGetClaimsPanopticonRunDetailQuery(selectedRunId ?? '', {
    skip: !selectedRunId,
  });

  const [deleteRun, { isLoading: isDeletingRun }] =
    useDeleteClaimsPanopticonRunMutation();
  const [clearAll, { isLoading: isClearingAll }] =
    useClearClaimsPanopticonMutation();

  const isMutating = isDeletingRun || isClearingAll;
  let confirmButtonLabel = 'Delete run';
  if (confirmAction?.kind === 'clear_all') {
    confirmButtonLabel = 'Clear all';
  }
  if (isMutating) {
    confirmButtonLabel = 'Working';
  }

  const handleConfirm = async () => {
    if (!confirmAction) return;

    try {
      if (confirmAction.kind === 'delete_run') {
        const result = await deleteRun(confirmAction.run.id).unwrap();
        if (selectedRunId === confirmAction.run.id) {
          setSelectedRunId(null);
        }
        toast.success(
          `Deleted run ${result.workflow_id} with ${result.deleted_coverages} coverage analyses.`,
        );
      } else {
        const result = await clearAll().unwrap();
        setSelectedRunId(null);
        toast.success(
          `Cleared ${result.deleted_runs} runs, ${result.deleted_coverages} coverage analyses, and ${result.deleted_matters} matter summaries.`,
        );
      }
      setConfirmAction(null);
    } catch {
      toast.error('Panopticon admin action failed');
    }
  };

  const totalRuns = runs.length;
  const totalAttention = runs.reduce(
    (sum, run) => sum + run.needs_attention_count,
    0,
  );

  return (
    <div className="min-h-screen bg-zinc-50 px-6 py-8 dark:bg-zinc-950">
      <div className="mx-auto max-w-[1600px] space-y-8">
        <section className="overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          <div className="bg-[linear-gradient(135deg,rgba(217,119,6,0.14),rgba(17,24,39,0)_45%),linear-gradient(180deg,rgba(24,24,27,0.03),rgba(24,24,27,0))] px-6 py-6 dark:bg-[linear-gradient(135deg,rgba(245,158,11,0.18),rgba(17,24,39,0)_50%),linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0))]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-600 dark:border-zinc-700 dark:bg-zinc-950/50 dark:text-zinc-300">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Claims Panopticon
                </div>
                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                  Persisted run browser and table controls
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-600 dark:text-zinc-300">
                  Browse persisted panopticon runs, inspect coverage and matter
                  analyses, and clear stale snapshots without leaving the app.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  outline
                  onClick={() => {
                    refetchRuns();
                  }}
                  disabled={isFetchingRuns}
                >
                  <RefreshCw
                    data-slot="icon"
                    className={isFetchingRuns ? 'animate-spin' : undefined}
                  />
                  Refresh
                </Button>
                <Button
                  color="red"
                  onClick={() => {
                    setConfirmAction({ kind: 'clear_all' });
                  }}
                  disabled={runs.length === 0 || isMutating}
                >
                  <Trash2 data-slot="icon" />
                  Clear All Data
                </Button>
              </div>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <StatCard label="Runs loaded" value={totalRuns} />
              <StatCard
                label="Attention flags"
                value={totalAttention}
                tone={totalAttention > 0 ? 'attention' : 'default'}
              />
              <StatCard
                label="Selected coverages"
                value={selectedRun?.total_coverages ?? '—'}
              />
              <StatCard
                label="Selected matters"
                value={selectedRun?.total_matters ?? '—'}
              />
            </div>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[360px,minmax(0,1fr)]">
          <div className="rounded-[24px] border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                  Runs
                </h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                  Latest persisted panopticon snapshots
                </p>
              </div>
              {isLoadingRuns ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-3">
              {runs.length === 0 && !isLoadingRuns ? (
                <div className="rounded-2xl border border-dashed border-zinc-300 px-4 py-10 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
                  No persisted panopticon runs found.
                </div>
              ) : null}

              <div className="space-y-3">
                {runs.map((run) => {
                  const isSelected = run.id === selectedRunId;
                  return (
                    <button
                      key={run.id}
                      type="button"
                      onClick={() => {
                        setSelectedRunId(run.id);
                      }}
                      className={[
                        'w-full rounded-2xl border p-4 text-left transition',
                        isSelected
                          ? 'border-orange-300 bg-orange-50 shadow-sm dark:border-orange-800 dark:bg-orange-950/30'
                          : 'border-zinc-200 bg-zinc-50 hover:border-zinc-300 hover:bg-white dark:border-zinc-800 dark:bg-zinc-950 dark:hover:border-zinc-700 dark:hover:bg-zinc-900',
                      ].join(' ')}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-zinc-950 dark:text-zinc-50">
                            {run.workflow_id}
                          </div>
                          <div className="mt-1 font-mono text-[11px] text-zinc-500 dark:text-zinc-400">
                            {run.id}
                          </div>
                        </div>
                        <Badge color={statusBadgeColor(run.status)}>
                          {run.status}
                        </Badge>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <Badge color="zinc">{run.run_scope}</Badge>
                        <Badge color="blue">{run.total_coverages} coverages</Badge>
                        <Badge color="green">{run.total_matters} matters</Badge>
                        {run.needs_attention_count > 0 ? (
                          <Badge color="orange">
                            {run.needs_attention_count} flagged
                          </Badge>
                        ) : null}
                      </div>

                      <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
                        {formatDateTime(run.created_at)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {!selectedRunId ? (
              <div className="rounded-[24px] border border-dashed border-zinc-300 bg-white px-6 py-16 text-center dark:border-zinc-700 dark:bg-zinc-900">
                <Workflow className="mx-auto h-10 w-10 text-zinc-400" />
                <h2 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                  Select a run
                </h2>
                <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                  Pick a persisted panopticon run to inspect its coverage and
                  matter outputs.
                </p>
              </div>
            ) : null}

            {selectedRunId && selectedRun ? (
              <>
                <section className="rounded-[24px] border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                  <div className="flex flex-col gap-4 border-b border-zinc-200 px-6 py-5 dark:border-zinc-800 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge color={statusBadgeColor(selectedRun.status)}>
                          {selectedRun.status}
                        </Badge>
                        <Badge color="zinc">{selectedRun.run_scope}</Badge>
                        <Badge color="blue">
                          Slack {selectedRun.slack_status}
                        </Badge>
                      </div>
                      <h2 className="mt-3 truncate text-2xl font-semibold text-zinc-950 dark:text-zinc-50">
                        {selectedRun.workflow_id}
                      </h2>
                      <p className="mt-2 font-mono text-xs text-zinc-500 dark:text-zinc-400">
                        Run ID {selectedRun.id}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        outline
                        onClick={() => {
                          refetchRuns();
                        }}
                        disabled={isFetchingDetail}
                      >
                        <RefreshCw
                          data-slot="icon"
                          className={isFetchingDetail ? 'animate-spin' : undefined}
                        />
                        Refresh Run
                      </Button>
                      <Button
                        color="red"
                        onClick={() => {
                          setConfirmAction({ kind: 'delete_run', run: selectedRun });
                        }}
                        disabled={isMutating}
                      >
                        <Trash2 data-slot="icon" />
                        Delete Run
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-3 px-6 py-5 md:grid-cols-2 xl:grid-cols-4">
                    <StatCard label="Coverages" value={selectedRun.total_coverages} />
                    <StatCard label="Matters" value={selectedRun.total_matters} />
                    <StatCard
                      label="Attention"
                      value={selectedRun.needs_attention_count}
                      tone={
                        selectedRun.needs_attention_count > 0
                          ? 'attention'
                          : 'default'
                      }
                    />
                    <StatCard
                      label="Failures"
                      value={
                        selectedRun.failed_coverages + selectedRun.failed_matters
                      }
                      tone={
                        selectedRun.failed_coverages + selectedRun.failed_matters > 0
                          ? 'attention'
                          : 'default'
                      }
                    />
                  </div>

                  <div className="grid gap-4 border-t border-zinc-200 px-6 py-5 xl:grid-cols-2 dark:border-zinc-800">
                    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-950">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                        Window
                      </div>
                      <div className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
                        {formatDateTime(selectedRun.source_window_started_at)} to{' '}
                        {formatDateTime(selectedRun.source_window_ended_at)}
                      </div>
                      <div className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                        Created
                      </div>
                      <div className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
                        {formatDateTime(selectedRun.created_at)}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-950">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                        Workflow run id
                      </div>
                      <div className="mt-2 font-mono text-xs text-zinc-700 dark:text-zinc-300">
                        {selectedRun.workflow_run_id || '—'}
                      </div>
                      <div className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                        Error
                      </div>
                      <div className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
                        {truncate(selectedRun.error, 220)}
                      </div>
                    </div>
                  </div>

                  {selectedRun.error ? (
                    <div className="border-t border-zinc-200 px-6 py-4 dark:border-zinc-800">
                      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                          <span>{selectedRun.error}</span>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </section>

                {isLoadingDetail ? (
                  <div className="rounded-[24px] border border-zinc-200 bg-white px-6 py-16 text-center shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                    <Loader2 className="mx-auto h-8 w-8 animate-spin text-zinc-400" />
                  </div>
                ) : null}

                {runDetail ? (
                  <>
                    <section className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Database className="h-4 w-4 text-zinc-500" />
                        <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                          Coverage analyses
                        </h3>
                        <Badge color="zinc">
                          {runDetail.coverage_analyses.length}
                        </Badge>
                      </div>
                      <div className="space-y-4">
                        {runDetail.coverage_analyses
                          .slice(
                            (coveragePage - 1) * COVERAGES_PER_PAGE,
                            coveragePage * COVERAGES_PER_PAGE,
                          )
                          .map((analysis) => (
                            <CoverageRow key={analysis.id} analysis={analysis} />
                          ))}
                      </div>
                      <PaginationControls
                        page={coveragePage}
                        totalPages={Math.ceil(
                          runDetail.coverage_analyses.length / COVERAGES_PER_PAGE,
                        )}
                        onPageChange={setCoveragePage}
                      />
                    </section>

                    <section className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Workflow className="h-4 w-4 text-zinc-500" />
                        <h3 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                          Matter summaries
                        </h3>
                        <Badge color="zinc">
                          {runDetail.matter_summaries.length}
                        </Badge>
                      </div>
                      <div className="space-y-4">
                        {runDetail.matter_summaries
                          .slice(
                            (matterPage - 1) * MATTERS_PER_PAGE,
                            matterPage * MATTERS_PER_PAGE,
                          )
                          .map((summary) => (
                            <MatterSummaryRow key={summary.id} summary={summary} />
                          ))}
                      </div>
                      <PaginationControls
                        page={matterPage}
                        totalPages={Math.ceil(
                          runDetail.matter_summaries.length / MATTERS_PER_PAGE,
                        )}
                        onPageChange={setMatterPage}
                      />
                    </section>

                    <section className="grid gap-4 xl:grid-cols-2">
                      <JsonPanel
                        label="Run input payload"
                        value={runDetail.run.input_payload}
                      />
                      <JsonPanel
                        label="Run result payload"
                        value={runDetail.run.result_payload}
                      />
                    </section>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </section>
      </div>

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmAction(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.kind === 'clear_all'
                ? 'Clear all panopticon data?'
                : 'Delete this panopticon run?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.kind === 'clear_all'
                ? 'This will remove every persisted panopticon run, coverage analysis, and matter summary.'
                : 'This will remove the selected run and all of its persisted coverage and matter records.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={isMutating}>
              {isMutating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {confirmButtonLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
